/*******************************************************************
 * ADOBE CONFIDENTIAL
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {clsx} from 'clsx';
import {space, style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {breakpoint} from '../../breakpoints/breakpoint-macro' with {type: 'macro'};
import {Skeleton} from '../utils/Skeleton';

const SKELETON_HERO_BACKGROUND_BORDER_RADIUS = '16px 16px 0 0';
const SKELETON_HERO_FADE_START_PERCENT = 16.76;
const SKELETON_PROMPT_BAR_HEIGHT_PX = 150;

// ─── Hero ─────────────────────────────────────────────────────────

const heroSkeletonLayoutClass = style({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'start',
	paddingBottom: {
		default: 32,
		[breakpoint('s')]: 40,
		[breakpoint('m')]: 40,
		[breakpoint('l')]: 40,
		[breakpoint('xl')]: 40,
		[breakpoint('xxl')]: 40,
	},
	paddingTop: {
		default: 32,
		[breakpoint('s')]: 40,
		[breakpoint('m')]: 48,
		[breakpoint('l')]: 56,
		[breakpoint('xl')]: 64,
		[breakpoint('xxl')]: space(72),
	},
	paddingX: {
		default: 16,
		[breakpoint('s')]: 20,
		[breakpoint('m')]: 20,
		[breakpoint('l')]: 24,
		[breakpoint('xl')]: 32,
		[breakpoint('xxl')]: 40,
	},
	boxSizing: 'border-box',
	width: 'auto',
});

const heroSkeletonGapClass = style({
	gap: {
		default: 12,
		[breakpoint('s')]: 0,
		[breakpoint('m')]: 0,
		[breakpoint('l')]: 0,
		[breakpoint('xl')]: 0,
		[breakpoint('xxl')]: 0,
	},
});

const heroPromptBarWidthClass = style({
	width: 'full',
	maxWidth: {
		default: 500,
		[breakpoint('s')]: 560,
		[breakpoint('m')]: 560,
		[breakpoint('l')]: 845,
		[breakpoint('xl')]: 845,
		[breakpoint('xxl')]: 925,
	},
	marginX: 'auto',
	backgroundColor: '[var(--ah-skl-layer-2)]',
	opacity: 0.6,
});

/**
 * Title skeleton wrapper — same 72px total height as the real heading so the
 * prompt bar doesn't shift when the skeleton swaps to real content.
 * Flex-centers the 16px bar vertically (implicit 28px top+bottom padding).
 */
const heroTitleSkeletonClass = style({
	width: 'full',
	marginX: 'auto',
	flexShrink: 0,
	height: 72,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

/**
 * Title skeleton bar — matches prompt bar's color token + opacity exactly.
 * Width per design: 280px at XS/M, 400px at S/L+.
 */
const heroTitleSkeletonBarClass = style({
	width: {
		default: 280,
		[breakpoint('s')]: 400,
		[breakpoint('m')]: 280,
		[breakpoint('l')]: 400,
	},
	height: 16,
	borderRadius: '[10px]',
	backgroundColor: '[var(--ah-skl-layer-2)]',
	opacity: 0.6,
	flexShrink: 0,
});

export class HomeHeroSkeleton extends Skeleton {
	public toString(): string {
		const promptBarStyle = `height:${SKELETON_PROMPT_BAR_HEIGHT_PX}px;border-radius:20px;`;

		return this.wrapper({
			className: clsx(heroSkeletonLayoutClass, heroSkeletonGapClass),
			children: `
				<div class="${heroTitleSkeletonClass}">
					<div class="${heroTitleSkeletonBarClass}"></div>
				</div>
				<div class="${heroPromptBarWidthClass}" style="${promptBarStyle}"></div>
			`,
		});
	}
}

// ─── Category filter tabs ───────────────────────────────────────────

/**
 * Five horizontal tab pills — visible at M+.
 * At XS/S shows a picker-style row (title + dropdown placeholder).
 */
export class HomeFiltersSkeleton extends Skeleton {
	public toString(): string {
		// Tab pill: single 80×32 pulsing element (no inner layer)
		const tabPill = this.rect({width: 80, height: 32, radius: 16});

		// Picker row for XS/S: short title placeholder + dropdown pill
		const pickerRow = `
			<div class="${style({
				display: {default: 'flex', [breakpoint('m')]: 'none'},
				flexDirection: 'row',
				alignItems: 'center',
				justifyContent: 'space-between',
				height: 48,
			})}">
				${this.rect({width: 80, height: 12, radius: 8})}
				${this.rect({width: 120, height: 32, radius: 8})}
			</div>
		`;

		// Tabs row for M+
		const tabsRow = `
			<div class="${style({
				display: {default: 'none', [breakpoint('m')]: 'flex'},
				flexDirection: 'row',
				gap: 16,
				height: 48,
				alignItems: 'center',
			})}">
				${this.repeat(5, tabPill)}
			</div>
		`;

		return this.wrapper({
			children: `
				${pickerRow}
				${tabsRow}
			`,
		});
	}
}

// ─── Action-card sections ──────────────────────────────────────────────────

/**
 * Square doodlebug/action tile.
 *
 * Width by breakpoint:
 *   XS  232px fixed (row clips / horizontal scroll)
 *   S   flex-grow (fills equal share)
 *   M   168px fixed
 *   L+  flex-grow (fills equal share)
 *
 * Height is always driven by aspect-ratio:1/1 (set via inline style).
 */
export class HomeActionCardSkeleton extends Skeleton {
	public toString(): string {
		const textBar = this.rect({width: '100%', height: 12, radius: 8});

		return `
			<div
				class="${style({
					backgroundColor: '[var(--ah-skl-layer-2)]',
					borderRadius: '[16px]',
					padding: 16,
					boxSizing: 'border-box',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'end',
					overflow: 'hidden',
					flexGrow: {default: 0, [breakpoint('s')]: 1},
					flexShrink: {default: 0, [breakpoint('s')]: 1},
					width: {default: '[232px]', [breakpoint('s')]: 'auto'},
					minWidth: {default: '[232px]', [breakpoint('s')]: 0},
				})}"
				style="aspect-ratio:1/1;"
			>
				${textBar}
			</div>
		`;
	}
}

/**
 * Doodlebug section: section heading + row of 3 action cards.
 * Row clips at XS (overflow:hidden), expands normally at S+.
 */
export class HomeDoodlebugSectionSkeleton extends Skeleton {
	public toString(): string {
		const heading = this.rect({width: 150, height: 16, radius: 10});

		return `
			<div class="${style({display: 'flex', flexDirection: 'column', gap: 16})}">
				${heading}
				<div class="${style({
					display: 'flex',
					flexDirection: 'row',
					gap: 16,
					overflow: {default: 'hidden', [breakpoint('s')]: 'visible'},
				})}">
					${this.repeat(3, new HomeActionCardSkeleton(this.props).toString())}
				</div>
			</div>
		`;
	}
}

/**
 * Featured / Create grid: heading + square cards.
 *   XS/S/M  2-column grid
 *   L+      4-column grid
 */
export class HomeFeaturedGridSkeleton extends Skeleton {
	public toString(): string {
		const heading = this.rect({width: 150, height: 16, radius: 10});
		const textBar = this.rect({width: '100%', height: 12, radius: 8});

		const card = `
			<div
				class="${style({
					backgroundColor: '[var(--ah-skl-layer-2)]',
					borderRadius: '[16px]',
					padding: 16,
					boxSizing: 'border-box',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'end',
					overflow: 'hidden',
				})}"
				style="aspect-ratio:1/1;"
			>
				${textBar}
			</div>
		`;

		return `
			<div class="${style({display: 'flex', flexDirection: 'column', gap: 16})}">
				${heading}
				<div class="${style({
					display: 'grid',
					gridTemplateColumns: {
						default: 'repeat(2, minmax(0, 1fr))',
						[breakpoint('l')]: 'repeat(4, minmax(0, 1fr))',
					},
					gap: 16,
				})}">
					${this.repeat(4, card)}
				</div>
			</div>
		`;
	}
}

/**
 * Suggested-cards / icon-card section: heading + tall cards with 30×30 icon.
 *
 * Mirrors SuggestedCardsBlade layout:
 *   XS  horizontal scroll flex row
 *   S/M 2-column grid
 *   L+  3-column grid
 */
export class HomeIconCardRowsSkeleton extends Skeleton {
	public toString(): string {
		const heading = this.rect({width: 150, height: 16, radius: 10});
		const iconPh = this.rect({width: 30, height: 30, radius: 8});
		const textBar = this.rect({width: '100%', height: 12, radius: 8});

		const card = `
			<div class="${style({
				backgroundColor: '[var(--ah-skl-layer-2)]',
				borderRadius: '[16px]',
				padding: 16,
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				overflow: 'hidden',
				height: '[222px]',
				flexGrow: {default: 0, [breakpoint('s')]: 1},
				flexShrink: {default: 0, [breakpoint('s')]: 1},
				flexBasis: {default: 'auto', [breakpoint('s')]: 0},
				width: {default: '[320px]', [breakpoint('s')]: 'auto'},
				minWidth: {default: '[320px]', [breakpoint('s')]: 0},
				maxWidth: {default: '[320px]', [breakpoint('s')]: 'none'},
			})}">
				${iconPh}
				${textBar}
			</div>
		`;

		return `
			<div class="${style({display: 'flex', flexDirection: 'column', gap: 16})}">
				${heading}
				<div class="${style({
					display: {default: 'flex', [breakpoint('s')]: 'grid'},
					gridTemplateColumns: {
						[breakpoint('s')]: 'repeat(2, minmax(0, 1fr))',
						[breakpoint('l')]: 'repeat(3, minmax(0, 1fr))',
					},
					gap: 16,
					overflowX: {default: 'auto', [breakpoint('s')]: 'visible'},
				})}">
					${this.repeat(6, card)}
				</div>
			</div>
		`;
	}
}

// ─── Desktop app blade ────────────────────────────────────────────────────────

/**
 * Desktop app / Creative Cloud blade — M+ only.
 *
 * Mirrors CollapsibleDesktopAppBanner structure:
 *   • Header row: 40×40 brand icon, title+subtitle group, CTA button
 *   • Cards row: 2-col grid at M/default, flex row at L+
 *     Each card: 24×24 app icon + name bar + CTA bar (column layout at M+)
 *
 * Hidden at XS/S (mobile app shortcuts take this slot instead).
 */
export class HomeDesktopAppBladeSkeleton extends Skeleton {
	public toString(): string {
		// Hero panel: title (175px) + subtitle (200px) + primary CTA — matches heroPanelClass
		const titleBar = this.rect({width: 175, height: 24, radius: 10});
		const subtitleBar = this.rect({width: 200, height: 14, radius: 8});
		const heroCta = this.rect({width: 120, height: 32, radius: 16});

		// App card: 40×40 icon, name, description, open button — matches appCardClass
		const appIcon = this.rect({width: 40, height: 40, radius: 8});
		const appName = this.rect({width: '100%', height: 16, radius: 8});
		const appDesc = this.rect({width: '80%', height: 12, radius: 8});
		const appCta = this.rect({width: '100%', height: 32, radius: 16});

		const appCard = `
			<div class="${style({
				backgroundColor: '[var(--ah-skl-layer-1)]',
				borderRadius: '[12px]',
				padding: 16,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				gap: 16,
				flexGrow: 1,
				flexShrink: 1,
				flexBasis: '[0px]',
				minWidth: 0,
			})}">
				<div class="${style({display: 'flex', flexDirection: 'column', gap: 16, width: 'full'})}">
					${appIcon}
					<div class="${style({display: 'flex', flexDirection: 'column', gap: 4})}">
						${appName}
						${appDesc}
					</div>
				</div>
				${appCta}
			</div>
		`;

		return `
			<div class="${style({
				display: {default: 'none', [breakpoint('l')]: 'flex'},
				flexDirection: {default: 'column', [breakpoint('xl')]: 'row'},
				borderRadius: '[16px]',
				backgroundColor: '[var(--ah-skl-layer-2)]',
				overflow: 'hidden',
				height: {[breakpoint('xl')]: '[240px]'},
			})}">
				<div class="${style({
					padding: 32,
					flexShrink: 0,
					display: 'flex',
					flexDirection: 'column',
					gap: 16,
				})}">
					<div class="${style({display: 'flex', flexDirection: 'column', gap: 4})}">
						${titleBar}
						${subtitleBar}
					</div>
					${heroCta}
				</div>
				<div class="${style({
					display: {default: 'grid', [breakpoint('l')]: 'flex'},
					gridTemplateColumns: '[repeat(2,1fr)]',
					flexDirection: 'row',
					flexGrow: {default: 0, [breakpoint('xl')]: 1},
					gap: 16,
					alignItems: 'stretch',
					paddingTop: {default: 0, [breakpoint('xl')]: 16},
					paddingBottom: 16,
					paddingStart: 16,
					paddingEnd: 16,
				})}">
					${this.repeat(4, appCard)}
				</div>
			</div>
		`;
	}
}

// ─── Card grid ────────────────────────────────────────────────────────

/**
 * Full content area below the hero.
 * Category tabs → desktop app blade → doodlebug section → featured grid → icon card rows.
 * Outer paddingX and content-column maxWidth mirror CategoryTabsList's
 * appSectionsTabsContainerClass / appSectionsTabsListClass so all sections
 * align with the real category tabs at every breakpoint.
 */
export class HomeCardGridSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				flexShrink: 0,
				paddingX: {
					default: 20,
					[breakpoint('l')]: 24,
					[breakpoint('xl')]: 32,
				},
				paddingTop: 24,
				paddingBottom: {
					default: 24,
					[breakpoint('m')]: 48,
				},
				boxSizing: 'border-box',
				overflow: 'hidden',
			}),
			children: `
				<div class="${style({
					display: 'flex',
					flexDirection: 'column',
					gap: {default: 24, [breakpoint('m')]: 40},
					maxWidth: {
						[breakpoint('m')]: 800,
						[breakpoint('l')]: 960,
						[breakpoint('xl')]: 1080,
						[breakpoint('xxl')]: 1200,
					},
					marginX: 'auto',
					width: 'full',
				})}">
					${new HomeFiltersSkeleton(this.props).toString()}
					${new HomeDesktopAppBladeSkeleton(this.props).toString()}
					${new HomeDoodlebugSectionSkeleton(this.props).toString()}
					${new HomeFeaturedGridSkeleton(this.props).toString()}
					${new HomeIconCardRowsSkeleton(this.props).toString()}
				</div>
			`,
		});
	}
}

export class HomeMobileAppCardSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				gap: 8,
				width: 48,
				flexShrink: 0,
			}),
			children: `
				${this.image({aspect: {x: 1, y: 1}})}
				${this.caption()}
			`,
		});
	}
}

export class HomeMobileAppsSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				gap: 20,
				paddingX: 16,
				overflow: 'hidden',
				boxSizing: 'border-box',
			}),
			children: `
				<div class="${style({maxWidth: 103, flexShrink: 0})}">
					${this.heading({level: 6})}
				</div>
				<div class="${style({
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'stretch',
					justifyContent: 'start',
					gap: 20,
					maxWidth: 'max',
					flexShrink: 0,
				})}">
					${this.repeat(5, new HomeMobileAppCardSkeleton(this.props).toString())}
				</div>
			`,
		});
	}
}

export class HomeMainSkeleton extends Skeleton {
	private renderHeroBackgroundLayers(
		backgroundSrc: string,
		fadeLayer: string,
	): string {
		return `
			<div style="position:absolute;top:0;left:0;right:0;bottom:0;z-index:0;border-radius:${SKELETON_HERO_BACKGROUND_BORDER_RADIUS};overflow:hidden;">
				<div style="position:absolute;inset:0;background-image:url('${backgroundSrc}');background-size:cover;background-position:center top;background-repeat:no-repeat;"></div>
				<div style="position:absolute;inset:0;background:${fadeLayer};"></div>
			</div>
		`;
	}

	public toString(): string {
		const isDarkMode = this.theme === 'dark';
		const backgroundSrc = this.props.background;
		const fadeLayer = isDarkMode
			? `linear-gradient(180deg, rgba(17, 17, 17, 0) ${SKELETON_HERO_FADE_START_PERCENT}%, rgba(17, 17, 17, 1) 100%)`
			: `linear-gradient(180deg, rgba(255, 255, 255, 0) ${SKELETON_HERO_FADE_START_PERCENT}%, rgba(255, 255, 255, 1) 100%)`;

		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				flexGrow: 1,
				flexShrink: {default: 0, [breakpoint('m')]: 1},
				minWidth: 0,
				minHeight: 0,
				backgroundColor: '[var(--ah-skl-layer-1)]',
				borderTopRadius: {
					default: 'none',
					[breakpoint('m')]: 'xl',
				},
				overflow: 'hidden',
				position: 'relative',
			}),
			children: `
				<div class="${style({
					position: 'relative',
					width: 'full',
				})}">
					${
						backgroundSrc
							? this.renderHeroBackgroundLayers(backgroundSrc, fadeLayer)
							: ''
					}
					<div style="position: relative; z-index: 1;">
						${new HomeHeroSkeleton(this.props).toString()}
					</div>
				</div>
				<div class="${style({
					paddingBottom: 32,
					position: {
						default: 'relative',
						[breakpoint('m')]: 'absolute',
					},
					opacity: {
						default: 1,
						[breakpoint('m')]: 0,
					},
				})}">
					${new HomeMobileAppsSkeleton(this.props).toString()}
				</div>
				${new HomeCardGridSkeleton(this.props).toString()}
			`,
		});
	}
}

export class HomeFileCardSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				gap: 20,
			}),
			children: `
				${this.image({aspect: {x: 1, y: 1}})}
				<div class="${style({maxWidth: 90})}">
					${this.caption()}
				</div>
			`,
		});
	}
}

export class HomeFilesGridSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'center',
				paddingX: {
					default: 20,
					[breakpoint('m')]: '[26px]',
				},
				paddingTop: '[26px]',
				paddingBottom: '[72px]',
				gap: '[26px]',
				boxSizing: 'border-box',
			}),
			children: `
				<div class="${style({maxWidth: 103, flexShrink: 0, visibility: 'hidden'})}">
					${this.heading({level: 6})}
				</div>
				<div
					class="${style({
						display: 'grid',
						gridTemplateColumns: {
							default: 'repeat(auto-fit, minmax(125px, 1fr))',
							[breakpoint('m')]: 'repeat(auto-fit, minmax(96px, 1fr))',
						},
						columnGap: 16,
						rowGap: '[30px]',
						width: 'full',
						marginX: 'auto',
					})}"
				>
					${this.repeat(12, new HomeFileCardSkeleton(this.props).toString())}
				</div>
			`,
		});
	}
}

export class HomeSidebarSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				width: {
					default: 'full',
					[breakpoint('m')]: 320,
				},
				flexShrink: 0,
				minHeight: 0,
				backgroundColor: '[var(--ah-skl-layer-1)]',
				borderTopRadius: {
					default: 'none',
					[breakpoint('m')]: 'xl',
				},
			}),
			children: `
				${new HomeFilesGridSkeleton(this.props).toString()}
			`,
		});
	}
}

export class HomeSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: {
					default: 'column',
					[breakpoint('m')]: 'row',
				},
				alignItems: 'stretch',
				justifyContent: 'start',
				gap: {
					default: 0,
					[breakpoint('m')]: 12,
				},
				height: 'full',
				minWidth: 0,
				minHeight: 0,
			}),
			children: `
				${new HomeMainSkeleton(this.props).toString()}
				${new HomeSidebarSkeleton(this.props).toString()}
            `,
		});
	}
}
